import React, { useState } from 'react';
import { useSearchParams } from 'react-router';
import { setLscStreamPages, LscApiError } from '../../api';
import { useLeague } from '../../hooks/useLeague';
import { isMemberPreview, streamPagesSetting, type StreamPagesSetting } from '../../stream/streamPages';

export function StreamSurfaceChrome({
  leagueId,
  streamPages,
  canSet,
  isOperator,
}: {
  leagueId: string;
  streamPages?: string | null;
  canSet?: boolean;
  isOperator: boolean;
}) {
  const [params, setParams] = useSearchParams();
  const { refreshLeague } = useLeague();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const previewing = isMemberPreview(params.toString(), {
    isOwner: isOperator,
    isCommissioner: isOperator,
    isOperator,
  });
  const setting = streamPagesSetting(streamPages);

  if (!isOperator && !canSet) return null;

  function setPreview(on: boolean) {
    const next = new URLSearchParams(params);
    if (on) next.set('as', 'member');
    else next.delete('as');
    setParams(next);
  }

  async function flip(next: StreamPagesSetting) {
    setSaving(true);
    setError(null);
    try {
      await setLscStreamPages(leagueId, next);
      await refreshLeague();
    } catch (err) {
      setError(err instanceof LscApiError ? err.message : 'Could not update stream pages.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="flex flex-col gap-2" data-testid="stream-surface-chrome">
      {previewing ? (
        <p className="text-sm text-stream-muted" data-testid="stream-member-preview">
          Previewing as a member. This does not change your access.{' '}
          <button type="button" className="underline" onClick={() => setPreview(false)}>
            Exit preview
          </button>
        </p>
      ) : isOperator ? (
        <button
          type="button"
          className="w-fit text-sm text-stream-muted underline"
          data-testid="stream-preview-as-member"
          onClick={() => setPreview(true)}
        >
          Preview as member
        </button>
      ) : null}
      {canSet && !previewing ? (
        <div className="flex flex-col gap-1" data-testid="stream-pages-switch">
          <button
            type="button"
            className="w-fit text-sm text-stream-ink underline"
            disabled={saving}
            onClick={() => flip(setting === 'on' ? 'off' : 'on')}
          >
            {setting === 'on' ? 'Turn stream pages off' : 'Turn stream pages on'}
          </button>
          <p className="text-xs text-stream-muted">Founder only. Members see these pages when this is on.</p>
          {error ? <p className="text-sm text-red-300">{error}</p> : null}
        </div>
      ) : null}
    </div>
  );
}
